/**
 * [2023-11-03.0952 by Brian]
 *    Added the ability to use click events as well as the standard route
 *    navigation. Hopefully, it is backwards compatible.
 */
import { ScrollArea, ScrollBar } from '@/libs/components/src/lib/ui/scroll-area';
import { Tabs as ShadcnTabs, TabsList, TabsTrigger } from '@chiroup/components/ui/tabs';
import React, { ChangeEvent, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';

export type Tab = {
  name: string;
  href?: string;
  active?: boolean;
  id?: number | string;
  transparent?: boolean;
  locked?: boolean;
};

type Props = {
  tabs: Tab[];
  rightSide?: React.ReactNode;
  onClick?: (e: ChangeEvent<HTMLSelectElement>, idx?: number) => void;
  transparent?: boolean;
};

const Tabs: React.FC<Props> = ({ tabs, rightSide, onClick, transparent = false }) => {
  const [selectedValue, setSelectedValue] = React.useState<number>(0);
  const navigate = useNavigate();
  const useClickEvent = !!onClick;

  const handleClickEvent = onClick
    ? onClick
    : (_: ChangeEvent<HTMLSelectElement>, __?: number) => {
        console.warn('Tabs: no onClick.');
      };

  const handleLinkChange = (e: ChangeEvent<HTMLSelectElement>) => {
    navigate(e.target.value, { replace: true });
  };

  const val = useMemo(() => {
    if (useClickEvent) {
      // const value = tabs.find((tab) => tab.active)?.href;
      return selectedValue;
    }

    const value = tabs.find(tab => tab.active)?.href;

    return value || 'General';
  }, [tabs, selectedValue, useClickEvent]);

  if (!tabs.length) {
    return null;
  }

  return (
    <div>
      <div className="sm:hidden flex justify-center">
        <nav className="w-full flex justify-center relative">
          {Boolean(tabs.length) && (
            <select
              className="relative left-0 mt-4 block w-full rounded-md border-gray-300 text-base focus:border-primary-600 focus:outline-none focus:ring-primary-600 sm:text-md mx-3"
              onChange={(e: any) => {
                (useClickEvent ? handleClickEvent : handleLinkChange)(e);
                setSelectedValue(e.target.value);
              }}
              value={val}
            >
              {(tabs || []).map((tab, tabIdx) => (
                <option
                  className="absolute left-0 z-10"
                  value={useClickEvent ? tabIdx : tab.href}
                  key={useClickEvent ? tabIdx : tab.href}
                >
                  {tab.name}
                </option>
              ))}
            </select>
          )}
        </nav>
      </div>

      <ScrollArea className="w-full hidden sm:block">
        <ShadcnTabs
          defaultValue={tabs[0].href || ''}
          className="w-full border-b border-gray-300 dark:border-dark-gray-800"
        >
          <TabsList
            className={cn(
              'w-full p-0 justify-start rounded-none px-4 h-12',
              transparent ? 'bg-transparent' : 'bg-background dark:bg-dark-gray-900'
            )}
          >
            {useClickEvent
              ? tabs.map((tab, tabIdx) => (
                  <TabsTrigger
                    key={`${tabIdx}-${tab.href}`}
                    value={tab.href || ''}
                    onClick={(e: any) => {
                      handleClickEvent(e, tabIdx);
                    }}
                    className={cn(
                      'h-12 flex items-center px-4 rounded-none border-b-2',
                      transparent ? 'bg-transparent' : 'bg-background',
                      tab.active
                        ? 'border-gray-700 dark:border-dark-gray-300 data-[state=active]:border-primary data-[state=active]:shadow-none '
                        : 'border-transparent hover:border-gray-300 dark:hover:border-dark-gray-400'
                    )}
                  >
                    <span className="text-sm whitespace-nowrap">{tab.name}</span>
                  </TabsTrigger>
                ))
              : tabs.map((tab, tabIdx) => (
                  <Link
                    key={`${tabIdx}-${tab.href}`}
                    to={tab.href || '/'}
                    className={[
                      'h-12 flex items-center gap-2 px-4 rounded-none data-[state=active]:shadow-none border-b-2 data-[state=active]:border-primary',
                      transparent ? 'bg-transparent' : 'bg-background dark:bg-dark-gray-900',
                      tab.active
                        ? 'border-gray-700 dark:border-dark-gray-300'
                        : 'border-transparent hover:border-gray-300 dark:hover:border-dark-gray-400',
                    ].join(' ')}
                  >
                    <span className="text-sm whitespace-nowrap">{tab.name}</span>
                    {tab.locked && (
                      <svg
                        className="h-3 w-3 text-gray-400 dark:text-gray-500 ml-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    )}
                  </Link>
                ))}
          </TabsList>
        </ShadcnTabs>
        {rightSide}
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
};

export default Tabs;
